// Session-scoped auth markers (sessionStorage: cleared when the browser closes).
//
// `activeSession` — set only after a fully authenticated login (incl. passing
//   2FA). PersistLogin uses it to decide whether a silent refresh is allowed.
// `pending2fa`    — set the moment a 2FA challenge is issued and held until the
//   OTP is verified (or the attempt is abandoned). While present it acts as an
//   auth LOCK: the silent-refresh path and route guards must treat the user as
//   unauthenticated, so a planted (pre-OTP) refresh cookie can't be used to
//   reach protected pages before the OTP step completes.

const ACTIVE_SESSION_KEY = "activeSession";
const PENDING_2FA_KEY = "auth:pending2fa";
const WAYA_OFFICIAL_UNLOCKED_KEY = "auth:wayaOfficialUnlocked";

const safe = (fn, fallback = undefined) => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

export const markActiveSession = () =>
  safe(() => sessionStorage.setItem(ACTIVE_SESSION_KEY, "1"));

export const clearActiveSession = () =>
  safe(() => sessionStorage.removeItem(ACTIVE_SESSION_KEY));

export const hasActiveSession = () =>
  safe(() => !!sessionStorage.getItem(ACTIVE_SESSION_KEY), false);

export const beginPending2fa = (email = "") =>
  safe(() => sessionStorage.setItem(PENDING_2FA_KEY, email || "1"));

export const clearPending2fa = () =>
  safe(() => sessionStorage.removeItem(PENDING_2FA_KEY));

export const hasPending2fa = () =>
  safe(() => !!sessionStorage.getItem(PENDING_2FA_KEY), false);

// Set only after otp-verify succeeds for the WayaQuick Official page. Lives
// for the browser session (same lifetime as activeSession) so an Owner isn't
// forced to re-OTP on every refresh, but a closed browser (or explicit
// logout) wipes it — next visit demands a fresh code again.

export const markWayaOfficialUnlocked = () =>
  safe(() => sessionStorage.setItem(WAYA_OFFICIAL_UNLOCKED_KEY, "1"));

export const clearWayaOfficialUnlocked = () =>
  safe(() => sessionStorage.removeItem(WAYA_OFFICIAL_UNLOCKED_KEY));

export const hasWayaOfficialUnlocked = () =>
  safe(() => !!sessionStorage.getItem(WAYA_OFFICIAL_UNLOCKED_KEY), false);
